const storageKey = 'membergy.notifications.v1'
const limit = 20
let fallback = null
let storageFailed = false

function read() {
  if (storageFailed) return fallback
  try {
    const stored = window.sessionStorage.getItem(storageKey)
    return stored ? JSON.parse(stored) : fallback
  } catch {
    storageFailed = true
    return fallback
  }
}

function write(snapshot) {
  fallback = snapshot
  if (storageFailed) return
  try {
    if (snapshot) window.sessionStorage.setItem(storageKey, JSON.stringify(snapshot))
    else window.sessionStorage.removeItem(storageKey)
  } catch {
    storageFailed = true
    // Soft navigation still works in memory when tab storage is unavailable.
    try {
      window.sessionStorage.removeItem(storageKey)
    } catch {
      // Access denial can also forbid removing the previous snapshot.
    }
  }
}

export function clearNotificationsWithoutHost() {
  if (!document.querySelector('[data-test="notifications"]')) write(null)
}

export default function notifications(context) {
  const timers = new Map()
  let onPageHide
  let onPageShow

  return {
    messages: {},
    duration: 5000,
    durationAfterHover: 2000,

    init() {
      this.restore()
      // Remove the payload from the live DOM so history snapshots cannot replay flash feedback.
      const flash = this.$el.getAttribute('data-notification-flash')
      this.$el.removeAttribute('data-notification-flash')
      if (flash) JSON.parse(flash).forEach(message => this.add(message))
      onPageHide = () => this.persist()
      onPageShow = event => { if (event.persisted) this.restore() }
      window.addEventListener('pagehide', onPageHide)
      window.addEventListener('pageshow', onPageShow)
    },

    destroy() {
      this.persist()
      timers.forEach(timer => clearTimeout(timer))
      timers.clear()
      window.removeEventListener('pagehide', onPageHide)
      window.removeEventListener('pageshow', onPageShow)
    },

    restore() {
      timers.forEach(timer => clearTimeout(timer))
      timers.clear()
      this.messages = {}
      const snapshot = read()
      if (snapshot?.context === context && Array.isArray(snapshot.messages)) {
        snapshot.messages.slice(-limit).forEach(message => {
          if (typeof message.key !== 'string' || !Number.isFinite(message.expiresAt)) return
          const remaining = Math.min(message.expiresAt - Date.now(), this.duration)
          if (remaining <= 0) return
          this.messages[message.key] = { ...message, lifetime: message.duration, isHovered: false }
          this.start(message.key, remaining, Math.min(100, remaining / message.duration * 100), message.expiresAt)
        })
      }
      this.persist()
    },

    persist() {
      const now = Date.now()
      const messages = Object.values(this.messages).slice(-limit).map(message => ({
        key: message.key,
        type: message.type,
        title: message.title,
        message: message.message,
        duration: message.isHovered ? this.durationAfterHover : message.lifetime,
        expiresAt: message.isHovered ? now + this.durationAfterHover : message.expiresAt
      })).filter(message => message.expiresAt > now)
      write({ context, messages })
    },

    clearTimers(key) {
      clearTimeout(timers.get(key))
      timers.delete(key)
    },

    start(key, duration, progress = 100, expiresAt = Date.now() + duration) {
      const message = this.messages[key]
      if (!message) return
      this.clearTimers(key)
      message.isHovered = false
      message.progress = progress
      message.duration = Math.max(0, expiresAt - Date.now())
      message.lifetime ??= duration
      message.expiresAt = expiresAt
      this.$nextTick(() => requestAnimationFrame(() => {
        if (this.messages[key] === message && !message.isHovered) message.progress = 0
      }))
      timers.set(key, setTimeout(() => this.remove(key), Math.max(0, expiresAt - Date.now())))
    },

    add(message) {
      if (!message || typeof message.message !== 'string') return
      if (Object.keys(this.messages).length >= limit) this.remove(Object.keys(this.messages)[0])
      const key = `${Date.now()}-${Math.random().toString(16).slice(2)}`
      this.messages[key] = {
        key,
        type: typeof message.type === 'string' ? message.type.slice(0, 30) : 'info',
        title: typeof message.title === 'string' ? message.title.slice(0, 1000) : '',
        message: message.message.slice(0, 8000)
      }
      this.start(key, this.duration)
      this.persist()
    },

    remove(key) {
      this.clearTimers(key)
      delete this.messages[key]
      this.persist()
    },

    hover(key) {
      if (!this.messages[key]) return
      this.clearTimers(key)
      this.messages[key].isHovered = true
      this.persist()
    },

    unhover(key) {
      if (!this.messages[key]) return
      this.messages[key].lifetime = this.durationAfterHover
      this.start(key, this.durationAfterHover)
      this.persist()
    },

    progressStyle(message) {
      return `width: ${message.progress}%; transition: width ${message.duration}ms linear;`
    }
  }
}
